import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { CoinUser, PaginatedResult, Role, SimpleTableService } from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService implements SimpleTableService {
  private httpClient = inject(HttpClient);
  private toast = inject(ToastrService);
  private loading = inject(LoadingService);
  private helperService = inject(HttpHelpersService);

  private _loadingUsers = false;

  queryPagedAndFilteredData = this.getPagedAndFilteredUsers;

  private getPagedAndFilteredUsers(
    page: number,
    size: number,
    searchText?: string,
    filterText?: string,
    sortText?: string
  ): Observable<PaginatedResult<CoinUser>> {
    return this.httpClient
      .get<
        PaginatedResult<CoinUser>
      >(`${environment.api.baseUrl}/admin/api/v2/master/users?Paging.Page=${page + 1}&Paging.Size=${size}${filterText}${sortText}`)
      .pipe(
        catchError(() => of(null)) // do not cancel subscription in simple table component
      );
  }

  public getAllUsers(): Observable<CoinUser[]> {
    return this.httpClient.get<PaginatedResult<CoinUser>>(`${environment.api.baseUrl}/admin/api/v2/master/users`).pipe(
      map(data => data.content),
      this.helperService.handleError('Error retrieving user data.'),
      finalize(() => {
        this._loadingUsers = false;
      })
    );
  }

  public getUserBySingleStringFilter(text: string): Observable<CoinUser[]> {
    if (!text || typeof text !== 'string') {
      return of(null);
    }

    const { firstName, lastName, gid } = this.disassembleFilterText(text);

    return this.getUsersByFilter(firstName, lastName);
  }

  public getUsersByFilter(firstName: string, lastName: string): Observable<CoinUser[]> {
    return this.httpClient
      .get<
        PaginatedResult<CoinUser>
      >(`${environment.api.baseUrl}/admin/api/v2/master/users?firstName=${firstName}&lastName=${lastName}`)
      .pipe(
        map(data => data.content),
        this.helperService.handleError('Error retrieving user data.'),
        finalize(() => {
          this._loadingUsers = false;
        })
      );
  }

  public queryUsersByGID(gid: string): Observable<PaginatedResult<CoinUser>> {
    return this.httpClient
      .get<PaginatedResult<CoinUser>>(`${environment.api.baseUrl}/admin/api/v2/master/users?gid=${gid}`)
      .pipe(
        this.helperService.handleError('Error retrieving user by gid.'),
        finalize(() => {
          this._loadingUsers = false;
        })
      );
  }

  public getUserByGID(gid: string): Observable<CoinUser> {
    return this.httpClient
      .get<PaginatedResult<CoinUser>>(`${environment.api.baseUrl}/admin/api/v2/master/users?gid=${gid}`)
      .pipe(
        map(data => data?.content?.[0]),
        this.helperService.handleError('Error retrieving user by gid.'),
        finalize(() => {
          this._loadingUsers = false;
        })
      );
  }

  public getUserById(id: string): Observable<CoinUser> {
    return this.httpClient.get<CoinUser>(`${environment.api.baseUrl}/admin/api/v2/master/users/${id}`).pipe(
      this.helperService.handleError('Error retrieving user by id.'),
      finalize(() => {
        this._loadingUsers = false;
      })
    );
  }

  private disassembleFilterText(text: string): { firstName: string; lastName: string; gid: string } {
    const textParts = text.trim().split(' ');
    const firstName: string = textParts[0];
    const lastName: string = textParts[1] || firstName;
    const gid: string = textParts[2] || lastName || firstName;

    return { firstName, lastName, gid };
  }

  public updateUser(user: CoinUser): Observable<CoinUser> {
    this.loading.present();
    return this.httpClient.put<CoinUser>(`${environment.api.baseUrl}/admin/api/v2/master/users/${user.id}`, user).pipe(
      this.helperService.handleError('Error updating user.'),
      tap(() => this.toast.success(`Successfully updated user`)),
      finalize(() => {
        this._loadingUsers = false;
        this.loading.dismiss();
      })
    );
  }

  public createUser(user: CoinUser): Observable<CoinUser[]> {
    return this.httpClient.post<CoinUser[]>(`${environment.api.baseUrl}/admin/api/v2/master/users`, user).pipe(
      this.helperService.handleError('Error creating user.'),
      tap(() => this.toast.success(`Successfully created user`)),
      finalize(() => {
        this._loadingUsers = false;
      })
    );
  }

  public deleteUserRoles(userId: string, roles: Role[]): Observable<CoinUser> {
    const roleIds = roles.map(role => role.id);
    return this.httpClient
      .request<CoinUser>('delete', `${environment.api.baseUrl}/admin/api/v2/master/users/${userId}/roles`, {
        body: {
          id: userId,
          roleIds
        }
      })
      .pipe(
        tap(() => this.toast.success(`Successfully updated roles`)),
        this.helperService.handleError('Error deleting user.')
      );
  }

  public updateUserRoles(userId: string, roles: Role[]): Observable<CoinUser> {
    const roleIds = roles.map(role => role.id);
    return this.httpClient
      .patch<CoinUser>(`${environment.api.baseUrl}/admin/api/v2/master/users/${userId}/roles`, {
        id: userId,
        roleIds
      })
      .pipe(this.helperService.handleError('Error updating user role.'));
  }

  public deleteUser(user: CoinUser): Observable<CoinUser[]> {
    return this.httpClient.delete<CoinUser[]>(`${environment.api.baseUrl}/admin/api/v2/master/users/${user.id}`).pipe(
      this.helperService.handleError('Error deleting user.'),
      tap(() => this.toast.success(`Successfully deleted user`)),
      finalize(() => {
        this._loadingUsers = false;
      })
    );
  }

  public addClusterParameters(user: CoinUser, clusterNodeId: string, roleId: string): Observable<void> {
    this.loading.present();
    return this.httpClient
      .patch<void>(`${environment.api.baseUrl}/admin/api/v2/master/users/${user.id}/parameters`, {
        id: user.id,
        clusterNodeId,
        roleId
      })
      .pipe(
        this.helperService.handleError('Error updating user parameters.'),
        finalize(() => {
          this.loading.dismiss();
        })
      );
  }

  public addRuleBasedAccessFilterParameters(
    user: CoinUser,
    ruleBasedAccessFilterId: string,
    roleId: string
  ): Observable<CoinUser> {
    this.loading.present();
    return this.httpClient
      .patch<CoinUser>(`${environment.api.baseUrl}/admin/api/v2/master/users/${user.id}/parameters`, {
        id: user.id,
        ruleBasedAccessFilterId,
        roleId
      })
      .pipe(
        this.helperService.handleError('Error updating user parameters.'),
        finalize(() => {
          this.loading.dismiss();
        })
      );
  }

  public deleteParameter(user: CoinUser, parameterId: string): Observable<void> {
    this.loading.present();
    return this.httpClient
      .delete<void>(`${environment.api.baseUrl}/admin/api/v2/master/users/${user.id}/parameters/${parameterId}`)
      .pipe(
        this.helperService.handleError('Error deleting user parameters.'),
        finalize(() => {
          this.loading.dismiss();
        })
      );
  }

  public set loadingUsers(loadingUsers) {
    this._loadingUsers = loadingUsers;
  }

  public get loadingUsers(): boolean {
    return this._loadingUsers;
  }
}
